/* eslint-disable max-lines */
/* eslint-disable no-shadow */
import * as React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Drawer from '@base/Drawer/Drawer';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUid } from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import Dynamic from '@base/Dynamic/Dynamic';
import { mediaQuery, Spacings } from '@styles/index';
import HeaderUser from '@layout/Header/HeaderUser/HeaderUser';
import Actions from '@layout/Header/Actions';
import Logo from '@comp/Logo/Logo';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import useOfflineCentreEnabledGoals from '@api/hooks/citySpecificOfflineCentres/useOfflineCentreEnabledGoals';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import AquillaDrawer from 'aquilla/core/organisms/Drawer';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import Typography from 'aquilla/core/atoms/Typography';
import CityCentreCard from '@cont/CommonOfflineCentres/CityCentreCard';
import {
  getOfflineCentreDetails,
  fetchOfflineCityCentresOfAGoalV2
} from '@api/fetchers/citySpecificOfflineCentre';
import { segment } from '@actions/analytics';
import {
  CENTRE_DROPDOWN_SELECTED,
  OFFLINE_VISIT_THE_CENTRE
} from '@cont/CitySpecificOfflineCentres/constants';
import Cross from 'aquilla/icons/misc/Cross';
import Call from '@icon/Call';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import useTopology from '@api/hooks/topology/useTopology';
import { localStorageAvailable } from '@utils/localStorage';
import { getPathName } from '@cont/CitySpecificOfflineCentres/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import { SearchInput } from 'aquilla/core/molecules/Search';
import NoResults from 'aquilla/icons/emptyStates/NoResults';
import Tooltip from '@base/Tooltip/Tooltip';
import Divider from 'aquilla/core/atoms/Divider';
import { TTU_NUMBER_CLICKED } from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';

const StyledArrowDown = styled(KeyboardArrowDown)`
  transition: transform 400ms ease-in-out;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_2B};
  }
`;

const SelectCentreWrapper = styled.div`
  grid-column: span 24;
  display: flex;
  justify-content: center;
  margin-bottom: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    justify-content: left;
    width: 100%;
    position: absolute;
    z-index: 1;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_2B};
    top: 0;
    background: var(--color-base-1);
    border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
  }
`;
const SelectCentreH2 = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const Login = Dynamic(() => import('@cont/Login/Login'), { ssr: false });

const StyAquillaDrawer = styled(AquillaDrawer)`
  margin-top: ${Spacings.SPACING_18B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_16B};
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    }
    max-height: 90vh;
  }
`;
const StyDialog = styled(Dialog)`
  margin-top: ${Spacings.SPACING_18B};
  max-height: 82vh;
  min-height: 65vh;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_16B};
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: ${Spacings.SPACING_4B};
      padding-bottom: ${Spacings.SPACING_0B};
    }
    max-height: 90vh;
  }
`;

const SearchWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    width: 100%;
  }
`;

const DrawerWrapper = styled.div`
  width: 100%;
  align-items: center;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin: auto;
  justify-content: center;
  flex-direction: column;
  padding-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    width: 100%;
    justify-content: left;
    height: calc(100vh - 120px);
    overflow-y: scroll;
    margin-top: 58px;
    padding-bottom: 0px;
  }
`;

const RightContainer = styled.div`
  display: flex;
`;

const LeftDiv = styled.div`
  display: flex;
  gap: 0px;
`;

const CentreInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_3B};
  ${({ $open }) => ($open ? 'pointer-events:none;' : '')}
  :hover {
    background-color: var(--color-base-light-0);
  }

  ${({ $open }) =>
    $open ? 'background-color:var(--color-divider) !important;' : ''}

  transition: background 300ms ease-out;
  ${mediaQuery.uptoMobile} {
    ${({ $isLoggedIn }) => ($isLoggedIn ? ';' : 'max-width: 135px;')}
  }
`;

const StyH5 = styled(Typography)`
  margin-right: ${Spacings.SPACING_2B};
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Wrapper = styled.div`
  width: 100%;
  z-index: ${({ $imagesClicked }) => ($imagesClicked ? '-1' : '1150')};
  position: fixed;
  top: 0px;
`;

const Header = styled.header`
  height: 72px;
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 1001;
  background: var(--color-base-1);
  border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
  ${mediaQuery.uptoMobile} {
    ${({ $isBatchesOnTop, $isBatchesVisibleZus }) =>
      $isBatchesOnTop && $isBatchesVisibleZus
        ? 'border-bottom: 0px !important;'
        : ';'}
    display: block;
    width: 100%;
    height: 64px;
  }
`;

const Nav = styled.nav`
  width: 1136px;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;

  ${mediaQuery.uptoTablet} {
    width: 728px;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const StyledLogo = styled(Logo)`
  margin-right: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    grid-column: 1/2;
    margin-right: ${Spacings.SPACING_2B};
  }
`;

const StyDialogHeader = styled(DialogHeader)`
  padding-bottom: ${Spacings.SPACING_4B};
`;

const SubHeading = styled(Typography)`
  display: ${({ $hide }) => ($hide ? 'none' : '')};
`;

const GoalName = styled.span`
  color: var(--color-text-primary);
`;

const NoCitiesWrapper = styled.div`
  max-width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
`;
const NoCitiesContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 340px;
  height: 100%;
  width: 100%;
`;

const CoachMark = styled(Tooltip)``;

const CoachMarkTitle = styled(Typography)`
  font-size: ${Spacings.SPACING_4B};
  line-height: ${Spacings.SPACING_6B};
  color: var(--color-i-white);
  text-align: center;
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  margin-top: ${Spacings.SPACING_4B};
  text-align: center;
`;

const SubTitle = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
  color: var(--color-text-secondary);
  text-align: 'center';
`;

const ContactWrapper = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  align-items: center;
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const VisitCentreCTA = styled.div`
  text-align: center;
  transition: 0.3s ease;
  text-decoration: underline;
  margin-left: ${Spacings.SPACING_4B};
  cursor: pointer;
  &:hover {
    transform: translateY(-1px);
  }
`;
const Phone = styled.a`
  display: flex;
  width: fit-content;
  text-align: center;
  margin-right: ${Spacings.SPACING_4B};
`;

const ContactUs = ({ data, goalName, routeGoalUID }) => {
  const {
    selectedCentreBEData,
    isBookAVisitEnabledFromCMS,
    setBookAVisitClicked,
    setBookAVisitFlow
  } = useOfflineCentreStore();
  const { city, state, name: centreName } = selectedCentreBEData || {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  if (!data) return null;
  const { phoneNumber, ctaDeepLink, cta } = data || {};
  const onPhoneNumberClick = () => {
    segment.track(
      TTU_NUMBER_CLICKED,
      {
        inbound_phone_number: phoneNumber,
        batch_group_type: 'Offline'
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
  };
  const handleClick = () => {
    segment.track(OFFLINE_VISIT_THE_CENTRE, {
      centre_name: centreName,
      city,
      state,
      goal_name: goalName,
      goal_uid: routeGoalUID,
      block_position: 'Header'
    });
    if (!isLoggedIn) {
      openLoginDrawer();
      if (isBookAVisitEnabledFromCMS) setBookAVisitClicked(true);
    } else if (isBookAVisitEnabledFromCMS) {
      setBookAVisitFlow(true);
    } else window.open(ctaDeepLink);
  };
  return (
    <ContactWrapper>
      <Phone href={`tel:${phoneNumber}`} onClick={onPhoneNumberClick}>
        <Call
          size={Spacings.SPACING_5B}
          viewBox="0 -4 24 24"
          color="var(--color-text-primary)"
        />
        <Typography variant="h6">{phoneNumber}</Typography>
      </Phone>
      <Divider orientation="vertical" />
      <VisitCentreCTA href={ctaDeepLink} onClick={handleClick}>
        <Typography variant="h6">{cta}</Typography>
      </VisitCentreCTA>
    </ContactWrapper>
  );
};
function HeaderComponent({ className, showLogin, centreData }) {
  const { isMobile } = useWindowSize();
  const {
    imagesClicked,
    setDemoClassesClicked,
    setScholarShipBlockClicked,
    setDownloadBrochureClicked,
    userLocation,
    isBatchesOnTop,
    isBatchesVisibleZus,
    setCentreListData,
    setSelectedCentre,
    setCentresListData,
    setSelectedCentreFound
  } = useOfflineCentreStore();

  const { isOpen: showLoginDrawer, closeElement: closeLoginDrawer } =
    useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const onClose = () => {
    setDemoClassesClicked(false);
    setScholarShipBlockClicked(false);
    setDownloadBrochureClicked(false);
    closeLoginDrawer();
  };
  const {
    query: { goalUID: routeGoalUID, centreUID, redirection_enabled }
  } = useRouter();
  const router = useRouter();
  const userId = useMyInfo(getMeUid);
  const { isValidating: isUserIdValidating } = useMyInfo();

  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);

  const { topology: goalTopology } = useTopology(routeGoalUID);
  const { goalSlug, name: goalName } = goalTopology;

  const { offlineCentresGoalList } = useOfflineCentreEnabledGoals();
  const { isStandalonePage, data: centreBlocksData } = centreData || {};
  const batchFooterData = centreBlocksData?.find(
    (block) => block.name === 'stickyFooter'
  );
  const { centreName, cityName } = centreInfo || {};
  const [open, setOpen] = React.useState(false);
  const [centresList, setCentreList] = React.useState({});
  const [searchInput, setSearchInput] = React.useState('');
  const [centresLength, setCentresLength] = React.useState(0);
  const [citiesLength, setCitiesLength] = React.useState(0);
  const [hideSubHeading, setHideSubHeading] = React.useState(false);
  const [isCoachMarkOpen, setIsCoachMarkOpen] = React.useState(false);
  const coachRef = React.useRef(false);

  const handleOnChangeSearch = (changedValue) => {
    setSearchInput(changedValue);
  };

  const handleOnClearSearch = () => {
    setSearchInput('');
  };

  const onSuccessFulFetchingOfCityCentreOfAGoal = async (res) => {
    const { centres: centresList, defaultCentre, redirectedCentre } = res;
    setCentreListData(centresList);
    const selectedCentre = centresList?.find(
      (centre) => centre.uid === centreUID
    );
    setSelectedCentre(selectedCentre);
    if (!selectedCentre) setSelectedCentreFound('notFound');
    if (selectedCentre) setSelectedCentreFound('Found');
    setCentreList((prevState) => {
      return {
        ...prevState,
        [routeGoalUID]: centresList,
        defaultCentre
      };
    });
    setCentresListData(centresList);
    setCentresLength(centresList?.length);
    const citiesList = new Set();

    centresList?.map((centre) => {
      return citiesList.add(centre.city?.toLowerCase());
    });

    setCitiesLength(citiesList?.size);

    if (localStorageAvailable() && !localStorage.getItem('isPageVisited')) {
      localStorage.setItem('isPageVisited', true);
      setIsCoachMarkOpen(true);
      coachRef.current = true;
    }

    const redirectionEnabled = redirection_enabled?.toLowerCase() === 'true';

    if (!!redirectionEnabled && redirectedCentre?.uid !== '') {
      const fallbackRoute = `/goal/${goalSlug}/${routeGoalUID}/centre/${redirectedCentre?.centre_name}/${redirectedCentre?.uid}`;

      const { centreDeepLinkUrl, isStandalonePage } =
        await getOfflineCentreDetails(routeGoalUID, redirectedCentre?.uid);

      if (isStandalonePage) return;

      const centreCMSRoute = getPathName(centreDeepLinkUrl, fallbackRoute);

      pushRoute(
        '/goal/[goalSlug]/[goalUID]/centre/[centreSlug]/[centreUID]',
        centreCMSRoute
      );
    }
  };

  React.useEffect(() => {
    const url = new URL(window.location.href);
    const redirected = url.searchParams.get('selectcity');
    if (redirected) {
      setOpen(true);
    }
  }, []);

  React.useEffect(() => {
    const body = {
      goal_uid: routeGoalUID,
      user_uid: userId
    };
    if (userLocation && userLocation.lat) {
      body.lat = userLocation.lat;
      body.long = userLocation.long;
    }
    if (!isUserIdValidating && router.isReady) {
      fetchOfflineCityCentresOfAGoalV2({
        body
      }).then((res) => {
        onSuccessFulFetchingOfCityCentreOfAGoal(res);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routeGoalUID,
    offlineCentresGoalList,
    userLocation,
    isUserIdValidating,
    router.isReady
  ]);
  React.useEffect(() => {
    const handleEvent = () => {
      if (coachRef.current) {
        setIsCoachMarkOpen(false);
        coachRef.current = false;
      }
    };
    window.addEventListener('scroll', handleEvent);
    window.addEventListener('click', handleEvent);

    return () => {
      window.removeEventListener('scroll', handleEvent);
      window.removeEventListener('click', handleEvent);
    };
  }, []);
  const closeCentreListDrawer = () => {
    setTimeout(() => {
      setOpen(false);
      document.body.style.overflow = 'unset';
    }, 250);
  };

  const hideSubHeadingOnScroll = () => {
    const contentElement = document.querySelector('#dialog-content');
    if (contentElement.scrollTop > 50) return setHideSubHeading(true);
    return setHideSubHeading(false);
  };

  const onClickCentreInfo = () => {
    setOpen(!open);
    setIsCoachMarkOpen(false);
    segment.track(CENTRE_DROPDOWN_SELECTED, {
      centre_name: centreName,
      city: cityName,
      goal_uid: routeGoalUID
    });
    if (typeof window !== 'undefined' && window.document) {
      document.body.style.overflow = 'hidden';
    }
  };

  const getCentresListToBeRendered = () => {
    let centres = centresList[routeGoalUID];
    if (!centres?.length) return null;
    if (searchInput) {
      centres = centres.filter((centre) => {
        return (
          centre.city?.toLowerCase().includes(searchInput.toLowerCase()) ||
          centre.state?.toLowerCase().includes(searchInput.toLowerCase()) ||
          centre.name?.toLowerCase().includes(searchInput.toLowerCase())
        );
      });
    }
    return centres;
  };
  const renderNoCitiesFound = () => {
    const centres = getCentresListToBeRendered();
    if (centres?.length) return null;
    return (
      <NoCitiesContainer>
        <NoCitiesWrapper>
          <NoResults />
          <Title variant="h4">Sorry we are not there yet</Title>
          <SubTitle variant="p2">
            We are coming to a city near you soon!
          </SubTitle>
        </NoCitiesWrapper>
      </NoCitiesContainer>
    );
  };
  const renderGoalCentresOfACity = () => {
    const centres = getCentresListToBeRendered();
    if (!centres?.length) return null;

    const cityCards = centres.map((centre) => {
      return (
        <CityCentreCard
          data={centre}
          goalUID={routeGoalUID}
          key={centre.uid}
          closeDrawer={closeCentreListDrawer}
          isSelected={centre.uid === centreUID}
        />
      );
    });
    return cityCards;
  };

  const renderCoachMark = () => {
    return <CoachMarkTitle variant="h5">View more centres</CoachMarkTitle>;
  };
  return (
    <Wrapper $imagesClicked={imagesClicked}>
      {!isMobile ? (
        <StyDialog open={open} size="medium" close={closeCentreListDrawer}>
          <StyDialogHeader>
            <Cross onClick={closeCentreListDrawer} height={24} width={24} />
            <DialogHeading>Select a centre</DialogHeading>
            <SubHeading
              variant="h6"
              color="var(--color-text-secondary)"
              $hide={hideSubHeading}
            >
              {centresLength || 0} centres available around {citiesLength || 0}{' '}
              cities in India for{' '}
              <GoalName>
                {goalName || 'IIT JEE, NEET UG and Foundation'}{' '}
              </GoalName>
              preparation
            </SubHeading>
          </StyDialogHeader>
          <DialogContent onScroll={hideSubHeadingOnScroll} id="dialog-content">
            <SearchWrapper>
              <SearchInput
                value={searchInput}
                onChange={handleOnChangeSearch}
                onClear={handleOnClearSearch}
                placeholder="Search for cities and states"
              />
            </SearchWrapper>
            <DrawerWrapper>{renderGoalCentresOfACity()}</DrawerWrapper>
            {renderNoCitiesFound()}
          </DialogContent>
        </StyDialog>
      ) : (
        <StyAquillaDrawer
          anchor="top"
          open={open}
          onClose={closeCentreListDrawer}
          showCloseIcon={!isMobile}
        >
          <DrawerWrapper>
            <SelectCentreWrapper>
              <Cross onClick={closeCentreListDrawer} height={24} width={24} />
              <SelectCentreH2 variant="h5">Select a centre</SelectCentreH2>
            </SelectCentreWrapper>
            <SearchWrapper>
              <SearchInput
                value={searchInput}
                onChange={handleOnChangeSearch}
                onClear={handleOnClearSearch}
                placeholder="Search for cities and states"
              />
            </SearchWrapper>
            {renderGoalCentresOfACity()}
            {renderNoCitiesFound()}
          </DrawerWrapper>
        </StyAquillaDrawer>
      )}
      <Header
        className={className}
        $isBatchesOnTop={isBatchesOnTop}
        $isBatchesVisibleZus={isBatchesVisibleZus}
      >
        <Nav>
          <LeftDiv>
            {isStandalonePage ? <StyledLogo /> : <StyledLogo withoutText />}
            {centreName && !isStandalonePage && (
              <CoachMark
                borderRadius={Spacings.SPACING_2B}
                padding={Spacings.SPACING_4B}
                backgroundColor="var(--color-i-blue)"
                title={renderCoachMark()}
                open={isCoachMarkOpen}
              >
                <CentreInfoWrapper
                  onClick={onClickCentreInfo}
                  $open={open}
                  id="infoWrapper"
                  $isLoggedIn={isLoggedIn}
                >
                  <StyH5 variant="h5">{centreName}</StyH5>
                  <StyledArrowDown size={Spacings.SPACING_5B} $open={open} />
                </CentreInfoWrapper>
              </CoachMark>
            )}
          </LeftDiv>

          <RightContainer>
            <ContactUs
              data={batchFooterData?.value}
              goalName={goalName}
              routeGoalUID={routeGoalUID}
            />
            {!isLoggedIn && showLogin && (
              <Actions showModal isJoinForFreeCtaExperiment />
            )}
            {isLoggedIn && (
              <HeaderUser
                isCitySpecificOfflineCentre
                excludeOptions={['darkMode']}
              />
            )}
          </RightContainer>
        </Nav>
      </Header>
      {showLogin && (
        <Drawer login drawerState={showLoginDrawer} onClose={onClose}>
          <Login />
        </Drawer>
      )}
    </Wrapper>
  );
}

HeaderComponent.propTypes = {
  className: PropTypes.string
};
HeaderComponent.defaultProps = {
  className: ''
};

export default HeaderComponent;
